<template>
  <mailbox-settings-layout :name="name">
    <el-card class="mailbox-settings-manage-workflow">
      <template #header>
        <h3>Manage Workflow</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>

        <!-- Current workflow -->
        <div class="mailbox-settings-manage-workflow__workflows-header">
          <h4><i class="ddi ddi-workflows" />Current workflow</h4>
        </div>
        <entity-workflow-selected-single :workflow="currentWorkflow" :show-selected="!hasNewWorkflow" />
        <loading-placeholder v-if="isTogglingJobs" :lines="2" />
        <template v-else>
          <job-toggler
            v-if="$permissions.jobsFeatureEnabled && currentWorkflowHasJobs"
            v-model:jobs-enabled="isJobsEnabled"
            :status-job-mappings="currentWorkflow.listStatusJobMappings"
            :statuses="currentWorkflow.listStatuses"
            :jobs="currentWorkflow.listJobs"
            :entity-type="EEntities.MAILBOXES"
            show-warning-when-disabling
          />
        </template>
        <div class="dd-divider" />
        <!-- New workflow -->
        <div class="mailbox-settings-manage-workflow__workflows-header">
          <h4>
            <i class="ddi ddi-workflows" />Choose new workflow or 
            <a href="" class="just-a-link" @click.prevent="showCreateWorkflowDrawer=true">make a new one&hellip;</a>
          </h4>
          <p>
            Choose a new workflow for this mailbox. In the next step, you will map each status in the
            current workflow to a status in the new workflow.
            <strong>Changing the workflow will also update the statuses on all existing
              conversations, <router-link :to="RouteAssistant.getMailboxSettingsConversationRulesRoute($route.params.accountId, id)" class="just-a-link">conversation rules</router-link>,
              and custom filters.</strong>
          </p>
          <p><br></p>
        </div>
        <entity-workflow-selector v-model="newWorkflowID" :workflows="selectableWorkflows" :entity-type="EEntities.MAILBOXES" />
        <dd-button
          :is-disabled="!hasNewWorkflow"
          :is-loading="isLoadingStatuses"
          :color="ColorAssistant.MailboxColor"
          class="float-right"
          size="large"
          @click="getStatuses()"
        >Change Workflow&hellip;</dd-button>
        <div class="clearfix" />
        <!-- Modal mapper -->
        <change-workflow-modal
          v-model:show-modal="isMappingStatuses"
          :auto-map-statuses="!hasDataReliantOnStatuses"
          :entity-type="EEntities.MAILBOXES"
          :current-workflow="currentWorkflow"
          :new-workflow="newWorkflow"
          @statuses-mapped="(val) => changeWorkflow(val)"
        />
        <!-- Create workflow drawer -->
        <create-workflow-drawer 
          v-model:show-drawer="showCreateWorkflowDrawer"
          :entity-type="EEntities.MAILBOXES"
          @workflow-created="(id) => handleWorkflowCreation(id)"
        />
      </template>
    </el-card>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  IStatusForSelection,
  IWorkflowForSelection,
} from '@/api/types/response.types'
import {
  IMailboxesInputChangeWorkflow,
  IStatusMapping,
} from '@/api/types/request.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import WorkflowsAPI from '@/api/modules/workflows.api'

export default defineComponent({
  name: 'MailboxSettingsManageWorkflow',

  components: {
    MailboxSettingsLayout,
    EntitySettingsLoading,
  },

  data() {
    return {
      EEntities,
      RouteAssistant,
      ColorAssistant,
      isEmptyMailbox: false,
      isLoading: true,
      isLoadingStatuses: false,
      isTogglingJobs: false,
      isJobsEnabled: false,
      isMappingStatuses: false,
      showCreateWorkflowDrawer: false,
      name: '',
      workflows: [] as IWorkflowForSelection[],
      hasDataReliantOnStatuses: true,
      newWorkflowID: null as any,
      currentWorkflowID: null as any,
      newWorkflowStatuses: [] as IStatusForSelection[],
    }
  },

  head(): any {
    return {
      title: `Manage Workflow - ${this.name}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    currentWorkflow(): IWorkflowForSelection {
      return this.workflows.find(w => w.id === this.currentWorkflowID)
    },
    newWorkflow(): IWorkflowForSelection {
      return this.workflows.find(w => w.id === this.newWorkflowID)
    },
    selectableWorkflows(): IWorkflowForSelection[] {
      return this.workflows.filter(w => w.id !== this.currentWorkflowID)
    },
    hasNewWorkflow(): boolean {
      return this.newWorkflowID !== null
    },
    currentWorkflowHasJobs(): boolean {
      return this.currentWorkflow.listJobs.length > 0
    },
  },

  watch: {
    isJobsEnabled: {
      immediate: false,
      async handler(val) {
        if (this.isLoading) {
          return
        }
        try {
          this.isTogglingJobs = true
          const response = await MailboxesAPI.put_ToggleJobs(this.id, val)
          this.isTogglingJobs = false
          this.$messaging.showSuccess(response.message)
        } catch (error) {
          this.$error.emit(error, true)
        }
      },
    },
  },

  async created() {
    this.isLoading = true
    await this.loadInitialData()
    this.isLoading = false
  },

  methods: {
    async loadInitialData() {
      await Promise.all([this.getWorkflowInfo(), this.getWorkflows()])
      this.newWorkflowID = null
    },
    isSelected(workflow: IWorkflowForSelection): boolean {
      return this.newWorkflowID === workflow.id
    },
    selectWorkflow(workflow: IWorkflowForSelection) {
      this.newWorkflowID = workflow.id
    },
    async handleWorkflowCreation(id: number) {
      await this.getWorkflows()

      const selectedWorkflow = this.workflows.find(w => w.id === id)
      const selectedIndex = this.workflows.findIndex(w => w.id === id)

      this.workflows.splice(selectedIndex, 1)
      this.workflows.unshift(selectedWorkflow)

      this.newWorkflowID = id
    },
    async getWorkflowInfo() {
      try {
        const response = await MailboxesAPI.get_WithWorkflow(this.id)
        this.name = response.title
        this.currentWorkflowID = response.workflowID
        this.hasDataReliantOnStatuses = response.hasDataReliantOnStatuses
        this.isJobsEnabled = response.isJobsEnabled
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async getWorkflows() {
      try {
        const response = await MailboxesAPI.get_Workflows()
        this.workflows = response
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async getStatuses() {
      try {
        this.isLoadingStatuses = true
        this.newWorkflowStatuses = await WorkflowsAPI.get_Statuses(
          this.newWorkflowID,
        )
        this.isMappingStatuses = true
      } catch (error) {
        this.$error.emit(error)
      } finally {
        this.isLoadingStatuses = false
      }
    },
    async changeWorkflow(statusMappings: IStatusMapping[]) {
      try {
        const data = {
          newWorkflowID: this.newWorkflowID,
          listStatusMappings: statusMappings,
        } as IMailboxesInputChangeWorkflow

        const response = await MailboxesAPI.put_ChangeWorkflow(this.id, data)
        this.$messaging.showSuccess(response.message)
        this.isMappingStatuses = false
        this.loadInitialData()
      } catch (error) {
        this.$error.emit(error, true)
        this.isMappingStatuses = false
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-manage-workflow {
  &__workflows-header {
    padding-bottom: 10px;

    > a {
      @include lettering--p-sm--no-color;
    }

    .ddi-workflows {
      color: var(--color-text-third);
      padding-right: 8px;
      top: -2px;
    }
  }
}
</style>
